import type { UseField } from '@seek/forms-ui/lib/Field/useField';
import type { Language } from '@seek/forms-ui/lib/types';
import { required } from '@seek/validators-js';
import {
  Checkbox,
  Box,
  TextField,
  Textarea,
  FieldLabel,
  Stack,
  Text,
  FieldMessage,
} from 'braid-design-system';

import type { SubItem } from '../SubjectData/useSubjects';

type DynamicFormValues = Record<string, any>;

type TextFieldType = 'text' | 'number' | 'email';

const mapInputFieldType: Record<string, TextFieldType> = {
  inputName: 'text',
  inputPhone: 'number',
  inputEmail: 'email',
};

const renderFieldComponent = (field: SubItem, fieldHook: any) => {
  const { type, label, options, name } = field;
  switch (type) {
    case 'checkboxGroup':
      return (
        <Box>
          <Stack space="small">
            <FieldLabel htmlFor="checkboxGroup" label={label} />
            {options &&
              options.map((option) => (
                <Checkbox
                  key={option.label}
                  id={option.label}
                  checked={Boolean(fieldHook.value[option.label])}
                  onChange={() =>
                    fieldHook.onChange({
                      ...fieldHook.value,
                      [option.label]: !fieldHook.value[option.label],
                    })
                  }
                  label={option.label}
                />
              ))}
            {fieldHook.errorMessage && (
              <Box marginBottom="gutter">
                <FieldMessage
                  id="error-message"
                  tone="critical"
                  message={fieldHook.errorMessage}
                />
              </Box>
            )}
          </Stack>
        </Box>
      );
    case 'textarea':
      return (
        <Box>
          <Textarea
            id={name}
            message={fieldHook.errorMessage}
            tone={!fieldHook.valid ? 'critical' : 'neutral'}
            label={label}
            onChange={fieldHook.onChange}
            value={fieldHook.value}
          />
        </Box>
      );
    case 'inputPhone':
    case 'inputEmail':
    case 'inputName':
      return (
        <Box>
          {type === 'inputPhone' && (
            <Box marginBottom="gutter">
              <Text size="large" weight="medium">
                Contact for issue
              </Text>
            </Box>
          )}
          <TextField
            id={name}
            label={label}
            type={mapInputFieldType[type]}
            message={fieldHook.errorMessage}
            tone={!fieldHook.valid ? 'critical' : 'neutral'}
            value={fieldHook.value}
            onChange={fieldHook.onChange}
          />
        </Box>
      );
    default:
      return <>Invalid field</>;
  }
};

export const RenderField = ({
  field,
  useField,
}: {
  field: SubItem;
  useField: UseField<DynamicFormValues, Language>;
}) => {
  const requiredCheckedGroupValidator = {
    getErrorMessage: () => Promise.resolve('Required field'),
    test: ({ value: obj }: { value: object }) =>
      Object.values(obj).some((value) => value === true),
  };
  const validators = [];

  if (field.required) {
    validators.push(required);
  }

  if (field.required && field.type === 'checkboxGroup') {
    validators.pop();
    validators.push(requiredCheckedGroupValidator);
  }

  const fieldHook = useField({
    id: field.name,
    validators,
  });

  return renderFieldComponent(field, fieldHook);
};
